import React, { useState, useEffect } from 'react';
import { 
  collection, 
  onSnapshot, 
  doc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove, 
  increment,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  where
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const Blog = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [availableCategories, setAvailableCategories] = useState(['all']);
  const [pendingFilteredArticles, setPendingFilteredArticles] = useState([]); // Articles waiting to be shown
  const { currentUser, isAdmin } = useAuth();

  const ARTICLES_PER_PAGE = 10;

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load initial articles with real-time updates
  useEffect(() => {
    loadCategoriesFromDB();
    
    // Set up real-time listener for articles
    const unsubscribe = onSnapshot(
      query(collection(db, 'articles'), orderBy('createdAt', 'desc'), limit(ARTICLES_PER_PAGE)),
      (snapshot) => {
        const articlesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Update articles and categories
        setArticles(articlesData);
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        setHasMore(snapshot.docs.length === ARTICLES_PER_PAGE);
        
        // Extract unique categories from articles
        const categories = new Set(['all']);
        articlesData.forEach(article => {
          if (article.categories && Array.isArray(article.categories)) {
            article.categories.forEach(cat => categories.add(cat));
          } else if (article.category) {
            categories.add(article.category);
          }
        });
        
        const newCategories = Array.from(categories);
        setAvailableCategories(prev => {
          // Only update if categories actually changed
          if (JSON.stringify(prev) !== JSON.stringify(newCategories)) {
            return newCategories;
          }
          return prev;
        });
        
        // If current selected category no longer exists, switch to 'all'
        if (!newCategories.includes(selectedCategory)) {
          setSelectedCategory('all');
        }
        
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to articles changes:', error);
        setLoading(false);
      }
    );
    
    return () => unsubscribe();
  }, []);

  const loadCategoriesFromDB = async () => {
    try {
      const categoriesQuery = query(collection(db, 'categories'), orderBy('name'));
      const snapshot = await getDocs(categoriesQuery);
      
      if (!snapshot.empty) {
        const dbCategories = snapshot.docs.map(doc => doc.data().name);
        // Always include 'all' as the first category
        const allCategories = ['all', ...dbCategories];
        setAvailableCategories(prev => {
          // Only update if categories actually changed
          if (JSON.stringify(prev) !== JSON.stringify(allCategories)) {
            return allCategories;
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error loading categories from DB:', error);
      // Keep existing categories if there's an error
    }
  };

  // Reload articles when category changes
  useEffect(() => {
    if (selectedCategory === 'all') {
      return; // The main listener already handles 'all' category
    }
    
    setPendingFilteredArticles([]); // Reset pending articles when category changes
    setLoading(true);
    
    // Set up real-time listener for specific category
    const unsubscribe = onSnapshot(
      query(collection(db, 'articles'), orderBy('createdAt', 'desc')),
      (snapshot) => {
        const allArticles = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter articles for the selected category
        const filteredArticles = allArticles.filter(article => {
          if (article.categories && Array.isArray(article.categories)) {
            return article.categories.includes(selectedCategory);
          } else if (article.category) {
            return article.category === selectedCategory;
          }
          return false;
        });
        
        // Take first 10 for display, keep rest as pending
        const initialArticles = filteredArticles.slice(0, ARTICLES_PER_PAGE);
        const remainingArticles = filteredArticles.slice(ARTICLES_PER_PAGE);
        
        setArticles(initialArticles);
        setPendingFilteredArticles(remainingArticles);
        setHasMore(remainingArticles.length > 0);
        
        // Set lastVisible based on the last displayed article's position in original query
        if (initialArticles.length > 0) {
          const lastDisplayedArticle = initialArticles[initialArticles.length - 1];
          const lastDisplayedIndex = snapshot.docs.findIndex(doc => doc.id === lastDisplayedArticle.id);
          if (lastDisplayedIndex >= 0) {
            setLastVisible(snapshot.docs[lastDisplayedIndex]);
          }
        }
        
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to category articles:', error);
        setLoading(false);
      }
    );
    
    return () => unsubscribe();
  }, [selectedCategory]);

  const loadMoreArticles = async () => {
    if (!lastVisible || loadingMore) return;
    
    try {
      setLoadingMore(true);
      
      if (selectedCategory === 'all') {
        // Standard pagination for all articles
        const q = query(
          collection(db, 'articles'),
          orderBy('createdAt', 'desc'),
          startAfter(lastVisible),
          limit(ARTICLES_PER_PAGE)
        );
        
        const snapshot = await getDocs(q);
        const newArticles = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setArticles(prev => [...prev, ...newArticles]);
        setHasMore(snapshot.docs.length === ARTICLES_PER_PAGE);
        setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        
      } else {
        // For category filtering, show more from pending articles
        if (pendingFilteredArticles.length > 0) {
          const nextBatch = pendingFilteredArticles.slice(0, ARTICLES_PER_PAGE);
          const remainingPending = pendingFilteredArticles.slice(ARTICLES_PER_PAGE);
          
          setArticles(prev => [...prev, ...nextBatch]);
          setPendingFilteredArticles(remainingPending);
          setHasMore(remainingPending.length > 0);
        }
      }
      
      setLoadingMore(false);
    } catch (error) {
      console.error('Error loading more articles:', error);
      setLoadingMore(false);
    }
  };

  const handleLike = async (articleId, isLiked) => {
    if (!currentUser) return;

    // Optimistic update - immediately update the UI
    setArticles(prevArticles => 
      prevArticles.map(article => {
        if (article.id === articleId) {
          const newLikedBy = isLiked 
            ? (article.likedBy || []).filter(uid => uid !== currentUser.uid)
            : [...(article.likedBy || []), currentUser.uid];
          
          return {
            ...article,
            likedBy: newLikedBy,
            likesCount: (article.likesCount || 0) + (isLiked ? -1 : 1)
          };
        }
        return article;
      })
    );

    const articleRef = doc(db, 'articles', articleId);
    
    try {
      if (isLiked) {
        // Unlike
        await updateDoc(articleRef, {
          likedBy: arrayRemove(currentUser.uid),
          likesCount: increment(-1)
        });
      } else {
        // Like
        await updateDoc(articleRef, {
          likedBy: arrayUnion(currentUser.uid),
          likesCount: increment(1)
        });
      }
    } catch (error) {
      console.error('Error updating like:', error);
      // Revert optimistic update on error
      setArticles(prevArticles => 
        prevArticles.map(article => {
          if (article.id === articleId) {
            const revertedLikedBy = isLiked 
              ? [...(article.likedBy || []), currentUser.uid]
              : (article.likedBy || []).filter(uid => uid !== currentUser.uid);
            
            return {
              ...article,
              likedBy: revertedLikedBy,
              likesCount: (article.likesCount || 0) + (isLiked ? 1 : -1)
            };
          }
          return article;
        })
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-brown-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-brown-50">
      {/* Header */}
      <div className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Health & Wellness Blog</h1>
              <p className="text-gray-600 mt-2">Expert insights on Ayurvedic health and natural wellness</p>
            </div>
            <div className="flex gap-4">
              <Link
                to="/"
                className="px-4 py-2 text-green-600 hover:text-green-800 font-medium"
              >
                ← Back to Home
              </Link>
              {isAdmin && (
                <Link
                  to="/admin/articles"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  Manage Articles
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {availableCategories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-green-100'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Articles Grid */}
        {articles.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {loading 
                ? selectedCategory === 'all' 
                  ? 'Loading articles...' 
                  : `Loading ${selectedCategory} articles...`
                : 'No articles available'}
            </h3>
            <p className="text-gray-600 mb-4">
              {loading 
                ? 'Please wait while we fetch the latest content...'
                : selectedCategory === 'all'
                ? 'No articles have been published yet.'
                : `No articles found in the "${selectedCategory}" category.`
              }
            </p>
            {!loading && selectedCategory !== 'all' && (
              <button
                onClick={() => setSelectedCategory('all')}
                className="text-green-600 hover:text-green-800 font-medium"
              >
                View all articles
              </button>
            )}
            {isAdmin && !loading && (
              <div className="mt-4">
                <Link
                  to="/admin/articles"
                  className="inline-block px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  Create Your First Article
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  currentUser={currentUser}
                  onLike={handleLike}
                />
              ))}
            </div>
            
            {/* Load More Button */}
            {hasMore && (
              <div className="text-center mt-12">
                <button
                  onClick={loadMoreArticles}
                  disabled={loadingMore}
                  className={`px-8 py-3 rounded-lg font-medium transition-all duration-200 ${
                    loadingMore
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 hover:shadow-lg'
                  }`}
                >
                  {loadingMore ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
                      Loading more articles...
                    </div>
                  ) : (
                    `Load More ${selectedCategory === 'all' ? 'Articles' : `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Articles`}`
                  )}
                </button>
              </div>
            )}
            
            {/* Loading indicator for category filtering */}
            {selectedCategory !== 'all' && loading && (
              <div className="text-center mt-12">
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-green-600 border-t-transparent"></div>
                  <span>Loading {selectedCategory} articles...</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ArticleCard = ({ article, currentUser, onLike }) => {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [localComments, setLocalComments] = useState(article.comments || []);

  // Update local comments when article comments change (from real-time updates)
  useEffect(() => {
    setLocalComments(article.comments || []);
  }, [article.comments]);

  const isLiked = currentUser && article.likedBy?.includes(currentUser.uid);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser || !newComment.trim()) return;

    setSubmittingComment(true);
    
    const comment = {
      id: Date.now().toString(),
      text: newComment.trim(),
      author: currentUser.displayName || currentUser.email,
      authorId: currentUser.uid,
      createdAt: new Date()
    };
    
    try {
      // Optimistic update - immediately add comment to local state
      setLocalComments(prev => [...prev, comment]);
      const commentText = newComment.trim(); // Store original comment text
      setNewComment('');

      const articleRef = doc(db, 'articles', article.id);
      await updateDoc(articleRef, {
        comments: arrayUnion(comment)
      });

    } catch (error) {
      console.error('Error adding comment:', error);
      // Revert optimistic update on error
      setLocalComments(prev => prev.filter(c => c.id !== comment.id));
      // Restore the comment text so user can try again
      setNewComment(comment.text);
    }
    
    setSubmittingComment(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
      {article.imageUrl && (
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-48 object-cover"
        />
      )}
      
      <div className="p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex gap-1 flex-wrap">
            {(article.categories && Array.isArray(article.categories) 
              ? article.categories 
              : [article.category]).filter(Boolean).map((category, index) => (
              <span key={index} className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                {category}
              </span>
            ))}
          </div>
          <span className="text-xs text-gray-500">
            {article.createdAt?.toDate().toLocaleDateString()}
          </span>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-3">{article.title}</h3>
        <p className="text-gray-600 mb-4 line-clamp-3">{article.excerpt}</p>

        {/* Action Buttons */}
        <div className="flex items-center justify-between border-t pt-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => currentUser ? onLike(article.id, isLiked) : null}
              className={`flex items-center gap-1 ${
                isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
              } ${!currentUser ? 'cursor-default' : 'cursor-pointer'}`}
              title={!currentUser ? 'Sign in to like articles' : ''}
            >
              <span className="text-lg">{isLiked ? '❤️' : '🤍'}</span>
              <span className="text-sm">{article.likesCount || 0}</span>
            </button>

            <button
              onClick={() => setShowComments(!showComments)}
              className="flex items-center gap-1 text-gray-500 hover:text-blue-500"
            >
              <span className="text-lg">💬</span>
              <span className="text-sm">{localComments.length}</span>
            </button>
          </div>

          <Link
            to={`/blog/${article.id}`}
            className="text-green-600 hover:text-green-800 font-medium text-sm"
          >
            Read More →
          </Link>
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className="mt-4 border-t pt-4">
            {/* Add Comment Form */}
            {currentUser ? (
              <form onSubmit={handleCommentSubmit} className="mb-4">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  rows={2}
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || submittingComment}
                  className="mt-2 px-4 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                >
                  {submittingComment ? 'Adding...' : 'Comment'}
                </button>
              </form>
            ) : (
              <p className="text-sm text-gray-500 mb-4">
                <Link to="/login" className="text-green-600 hover:text-green-800">
                  Sign in
                </Link>{' '}
                to add comments
              </p>
            )}

            {/* Comments List */}
            <div className="space-y-3 max-h-40 overflow-y-auto">
              {localComments.map((comment) => (
                <div key={comment.id} className="text-sm">
                  <div className="font-medium text-gray-900">{comment.author}</div>
                  <div className="text-gray-600">{comment.text}</div>
                  <div className="text-xs text-gray-400">
                    {comment.createdAt?.toDate?.()?.toLocaleDateString() || 'Just now'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
